import React from 'react';
import { connect } from 'react-redux';
import { MdSyncProblem } from 'react-icons/md';

/**
 * Die Zeile, die sagt, was die stehengebliebenen Teilnahmen bedeuten.
 *
 * Das Modell dahinter ist bewusst schlicht: die Liste der erfassten Teilnahmen **ist** die
 * Warteschlange. Jede Teilnahme geht einzeln raus und verschwindet erst nach einer bestaetigten
 * Antwort; bricht die Schleife ab, bleibt der Rest unveraendert stehen und ein erneuter Druck
 * auf "verbindlich melden" setzt dort fort. Keine zweite Outbox, kein Wiederholungszaehler,
 * kein Zustand "gesendet, aber unbestaetigt".
 *
 * Nur war dieser Zustand bisher stumm. Ausser beim abgelaufenen Kennwort (401) wurde jeder
 * Abbruch verworfen: das Melden hoerte auf, ein paar Zeilen blieben stehen, und niemand sagte
 * dem Anwender, dass er noch einmal druecken soll. Genau diese eine Zeile fehlte - kein
 * Fehlerdialog, kein Statuscode im Text. Der Code steht im title-Attribut, fuer den Supportfall.
 *
 * **Warum drei Formulierungen und nicht eine.** "Bitte noch einmal druecken" ist nicht immer der
 * richtige Rat. Bei 401/403 hilft Wiederholen allein nicht, da muss die Berechtigung erneuert
 * werden. Und 406/422 gelten einer *bestimmten* Teilnahme (Pruefziffer, Punktewert,
 * Teilnahmedatum ausserhalb des Zeitraums) - die steht danach weiter an erster Stelle der
 * Warteschlange und wuerde bei jedem weiteren Versuch dieselbe Ablehnung kassieren. Ein
 * pauschales "einfach nochmal" waere dort eine Endlosschleife. Deshalb wird diese EFN benannt.
 */
const AUTORISIERUNG = [401, 403];
const ABGELEHNTE_TEILNAHME = [406, 422];

export const meldeversuchHinweis = (versuch, offeneEFNs = []) => {
	// Waehrend der Uebertragung steht im Merker nur `laeuft` - da ist noch nichts schiefgegangen,
	// und die Zeile schweigt.
	if(!versuch || !versuch.abgebrochen || offeneEFNs.length === 0) return null;
	const anzahl = offeneEFNs.length;
	const kopf = anzahl === 1
		? "Eine Teilnahme ist noch nicht gemeldet."
		: `${anzahl} Teilnahmen sind noch nicht gemeldet.`;
	// Bewusst ohne Rezept: bei 401 steht der Hinweis, wie man die Veranstaltung neu hinzufuegt,
	// schon oben im Kopfbereich. Zweimal rot dieselbe Anweisung liest niemand zweimal. Hier steht
	// nur, was es fuer die Warteschlange bedeutet.
	if(AUTORISIERUNG.includes(versuch.status)) return { kopf, farbe: "danger",
		satz: "Die Berechtigung für diese Veranstaltung wurde nicht angenommen – erneutes Melden "
			+ "allein hilft hier nicht." };
	// Nur solange die abgelehnte Teilnahme auch wirklich noch dasteht: wurde sie inzwischen
	// entfernt oder korrigiert, ist der alte Grund hinfaellig und der normale Rat wieder richtig.
	if(ABGELEHNTE_TEILNAHME.includes(versuch.status) && offeneEFNs.includes(versuch.efn))
		return { kopf, farbe: "danger",
			satz: `Die Meldung für EFN ${versuch.efn} hat der EIV nicht angenommen – `
				+ (versuch.status === 422
					? "die Angaben passen formal nicht (etwa Prüfziffer der EFN oder Punktewert)."
					: "die fachliche Prüfung ist fehlgeschlagen (etwa Teilnahmedatum außerhalb des Veranstaltungszeitraums).")
				+ " Sie steht vorn in der Warteschlange und hält die übrigen auf: bitte diese Zeile "
				+ "korrigieren oder entfernen und danach erneut melden." };
	return { kopf, farbe: "warning",
		satz: "Die Übertragung wurde unterbrochen. Es ist nichts verlorengegangen und nichts doppelt "
			+ "gemeldet – bitte noch einmal auf „verbindlich melden“ drücken." };
};

const MeldungUnterbrochenHinweis = (props) => {
	const { versuch, offeneEFNs } = props;
	const hinweis = meldeversuchHinweis(versuch, offeneEFNs);
	if(hinweis === null) return null;
	// Bewusst das nackte div mit den Bootstrap-Klassen statt reactstrap-Alert: dessen Tag setzt
	// role="alert" und laesst sich nicht ueberschreiben. Ein Alert unterbricht den Screenreader
	// mitten im Satz - fuer eine Zeile, die nur sagt "hier steht noch etwas offen", ist das zu
	// laut. role="status" mit aria-live="polite" wartet, bis der Vorleser fertig ist.
	return (
		<div className={`alert alert-${hinweis.farbe} py-2 mb-2 d-flex align-items-start gap-2`}
			role="status" aria-live="polite"
			title={versuch.status ? `Antwort des Servers: ${versuch.status}` : "keine Antwort vom Server"}>
			<MdSyncProblem fontSize="1.3em" className="flex-shrink-0 mt-1" aria-hidden="true" />
			<div><b>{hinweis.kopf}</b> {hinweis.satz}</div>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	versuch: state.punkteMeldung.meldeversuch[ownProps.vnr],
	offeneEFNs: Object.keys(state.punkteMeldung.erfassteTeilnahmen[ownProps.vnr] || {})
});

export default connect(mapStateToProps)(MeldungUnterbrochenHinweis);
